/** Editor для Iнвентаризації НЗВ (Порція 4b).
 *
 * Особливості (рішення власника 5):
 * - status: draft → posted → cancelled (односторонні переходи)
 * - draft: actions Зберегти + Провести
 * - posted: action Скасувати (cancel, не unpost)
 * - cancelled: read-only без actions
 * - Без acc.entries (рішення 5-A — snapshot аналітичний)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs as refsApi } from '@/api/refs';
import { wipInventory, type WipInventoryItemIn } from '@/api/wipInventory';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';

const PERMISSION = 'production.wip_inventory.manage';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  inventory_date: string;
  warehouse_id: string;
  comment: string;
  items: WipInventoryItemIn[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  inventory_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  comment: '',
  items: [],
};

const newRow = (n: number): WipInventoryItemIn => ({
  line_number: n, nomenclature_id: '', quantity_factual: '0',
});

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

export default function WipInventoryEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, totalAccessibleOrgs } = usePermissionsByOrg(PERMISSION);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refsApi.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const docQuery = useQuery({
    queryKey: ['wip-inventory', id],
    queryFn: () => wipInventory.get(id!),
    enabled: !isNew,
  });
  const numberPreview = useNumberPreview('doc.wip_inventory', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);
  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        inventory_date: d.inventory_date.slice(0, 16),
        warehouse_id: d.warehouse_id ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          quantity_factual: it.quantity_factual,
          uom_id: it.uom_id,
          cost_per_unit: it.cost_per_unit,
          total_cost: it.total_cost,
          comment: it.comment,
        })),
      });
    }
  }, [isNew, docQuery.data]);

  const status = docQuery.data?.status ?? 'draft';
  const isDraft = isNew || status === 'draft';
  const isPosted = status === 'posted';
  const isCancelled = status === 'cancelled';
  const readonly = isPosted || isCancelled;

  const buildPayload = () => ({
    document_number: form.document_number || null,
    document_date: new Date(form.document_date).toISOString(),
    inventory_date: new Date(form.inventory_date).toISOString(),
    warehouse_id: form.warehouse_id || null,
    comment: form.comment || null,
    items: form.items,
  });

  const create = useMutation({
    mutationFn: () => wipInventory.create({ organization_id: form.organization_id, ...buildPayload() }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['wip-inventory'] });
      navigate(`/wip-inventory/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => wipInventory.update(id!, buildPayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wip-inventory'] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: create → post одним кліком.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await wipInventory.create({ organization_id: form.organization_id, ...buildPayload() });
      await wipInventory.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['wip-inventory'] });
      navigate(`/wip-inventory/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: update → post одним кліком.
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await wipInventory.update(id!, buildPayload());
      await wipInventory.post(id!);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wip-inventory'] }),
    onError: (e) => setError(extractError(e)),
  });
  const cancel = useMutation({
    mutationFn: () => wipInventory.cancel(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wip-inventory'] }),
    onError: (e) => setError(extractError(e)),
  });

  function addItem() { setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] })); }
  function removeItem(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((it, idx) => ({ ...it, line_number: idx + 1 })),
    }));
  }
  function setItemField(i: number, patch: Partial<WipInventoryItemIn>) {
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  }

  if (!isNew && docQuery.isLoading) return <div>Завантаження…</div>;

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова інвентаризація НЗВ' : `Iнвентаризація НЗВ: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ Проведено</span>
          )}
          {isCancelled && (
            <span style={{ marginLeft: '0.75rem', color: '#64748b', fontSize: '1rem' }}>⊘ Скасовано</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/wip-inventory')}>Назад</button>
          {isDraft && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={() => (isNew ? create.mutate() : update.mutate())}
                      disabled={create.isPending || update.isPending}>Зберегти</button>
            </RequirePermission>
          )}
          {isNew && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={() => createAndPost.mutate()}
                      disabled={create.isPending || createAndPost.isPending}
                      title="Зберігає та проводить документ">▶ Провести</button>
            </RequirePermission>
          )}
          {!isNew && isDraft && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={() => updateAndPost.mutate()}
                      disabled={updateAndPost.isPending || update.isPending}
                      title="Зберігає поточні зміни та проводить документ">▶ Провести</button>
            </RequirePermission>
          )}
          {!isNew && isPosted && (
            <RequirePermission code={PERMISSION}>
              <button onClick={() => cancel.mutate()} disabled={cancel.isPending}>⊘ Скасувати</button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
        <div className="field">
          <label>Юрособа</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
            </small>
          )}
        </div>
        <div className="field">
          <label>Номер</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} disabled={readonly} />
        </div>
        <div className="field">
          <label>Дата документа</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })} disabled={readonly} />
        </div>
        <div className="field">
          <label>Дата інвентаризації</label>
          <DatePopover withTime value={form.inventory_date}
                 onChange={(v) => setForm({ ...form, inventory_date: v })} disabled={readonly} />
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} disabled={readonly}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (<option key={w.id} value={w.id}>{w.code} — {w.name}</option>))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })} disabled={readonly} />
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontWeight: 600 }}>Фактичні залишки у цеху</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Сировина</th>
            <th style={{ padding: '0.5rem', width: 100 }}>Фактична к-сть</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Собівартість</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Сума</th>
            <th style={{ padding: '0.5rem', width: 40 }} />
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select value={it.nomenclature_id}
                        onChange={(e) => setItemField(i, { nomenclature_id: e.target.value })} disabled={readonly}>
                  <option value="">—</option>
                  {nomQ.data?.items.map((n) => (<option key={n.id} value={n.id}>{n.code} — {n.name}</option>))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={it.quantity_factual}
                       onChange={(e) => setItemField(i, { quantity_factual: e.target.value })} disabled={readonly} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.cost_per_unit ?? '0'}
                       onChange={(e) => setItemField(i, { cost_per_unit: e.target.value })} disabled={readonly} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.total_cost ?? '0'}
                       onChange={(e) => setItemField(i, { total_cost: e.target.value })} disabled={readonly} />
              </td>
              <td style={{ padding: '0.5rem' }}>{!readonly && <button onClick={() => removeItem(i)}>×</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!readonly && <button onClick={addItem} style={{ marginTop: '0.5rem' }}>+ Додати рядок</button>}

      {error && <div className="error" style={{ marginTop: '1rem' }}>{error}</div>}
    </div>
  );
}
